import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
  {/*    <div className="mb-8">
        <Link to="/register">
          <Button variant="outline" size="sm">
            {t("back_to_registration") || "Back to Registration"}
          </Button>
        </Link>
      </div>*/}

      <div className="prose prose-slate max-w-none">
        <h1 className="text-3xl font-bold mb-6">{t("privacy_policy") || "Privacy Policy"}</h1>

        <p className="text-sm text-gray-600 mb-6">
          {t("last_updated") || "Last Updated"}: {new Date().toLocaleDateString()}
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. {t("introduction") || "Introduction"}</h2>
          <p className="mb-4">
            Welcome to Clarynext! Your privacy and the security of your data are very important to us. Clarynext is a platform designed to help you capture, share, apply, and learn thinking and behavior patterns in context. This policy explains what information we collect, how we use and protect it, and — importantly — how your information is shared within the platform.
            By using clarynext.com or the Clarynext app, you agree to the collection and use of information in accordance with this policy.  </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. {t("information_we_collect") || "Information We Collect"}</h2>

          <h3 className="text-xl font-semibold mb-3 mt-4">{t("personal_data") || "Personal Data"}</h3>
          <p className="mb-4">
            When you register for an account, we may collect personally identifiable information, such as:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Name</li>
            <li>Email address</li>
            <li>Password (stored securely in hashed format)</li>
            <li>Profile information you choose to provide</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 mt-4">{t("usage_data") || "Usage Data"}</h3>
          <p className="mb-4">
            <b>Engagement and Activity Data:</b> To power the core features of the platform, we collect information about your interactions. This includes the patterns you create, the activities you engage with, and how you apply them within the context of the service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. {t("how_we_use_your_information") || "How We Use Your Information"}</h2>
          <p className="mb-4">
            We use the information we collect in the following ways:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>To provide, operate, and maintain our application</li>
            <li>To improve, personalize, and expand our application by understanding how it's being used.</li>
            <li>To understand and analyze how you use our application</li>
            <li>To develop new products, services, features, and functionality</li>
            <li>To communicate with you, including for customer service and support</li>
          </ul>
          We <b>do not</b> sell your personal data to third parties.
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. {t("data_sharing") || "How Your Activity is Shared on Clarynext"}</h2>
          <p className="mb-4">
            Clarynext is a collaborative platform. By its nature, some of your activity is visible to other users to facilitate learning and sharing. It is crucial you understand how this works.
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><b>Default Visibility to Creators:</b> Please be aware that when you use or engage with a thinking pattern or activity created by another person,<b> the original creator may by default see your engagement with their content.</b> This is a core part of the platform's feedback and learning loop.
            </li>
            <li><strong>Direct Sharing:</strong>  Anyone you explicitly choose to share an engagement, pattern, or activity with will be able to see that content.</li>
          </ul>
<p>Because of this inherent visibility, it is essential that you review and adhere to the warnings in the sections below.
</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. {t("data_security") || "Data Security"}</h2>
          <p className="mb-4">
            We use administrative, technical, and physical security measures to help protect your personal information. While we have taken
            reasonable steps to secure the personal information you provide to us, please be aware that no security measures are perfect or
            impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
          </p>
          <ul>
            <li><b>Encryption: </b> All data transmitted between your device and our servers is encrypted using SSL/TLS, the same technology used by banks.</li>
         <li><b>Secure Database:</b> Your account information is stored in a secure database managed by MongoDB Atlas, a leading cloud service provider known for its robust security practices.
         </li>
            <li>
              <b>Beta Platform & Developer Access: </b>Please note that Clarynext is currently in beta and under active development. This means our team, including developers, may need to access user data on rare occasions. This access is strictly for purposes such as troubleshooting technical issues, resolving bugs, or maintaining the integrity of the platform. In such cases, we are committed to accessing the minimum amount of data necessary to resolve the issue and maintaining strict confidentiality.
            </li>
          </ul>
          <p>
            While we are committed to protecting your data, please remember that no method of transmission over the Internet or electronic storage is 100% secure.

          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. {t("disclaimers_and_responsibilities") || "⚠️ Important Disclaimers & Your Responsibilities"}</h2>
          <p className="mb-4">
            <b>Do Not Share Sensitive Information</b>
            Given that your engagements can be visible to others, we must reiterate our most important warning: Do Not Share Sensitive Information.

            Clarynext is a platform for general guidance and connection, not for sharing confidential data. For your own protection, we strongly warn you against disclosing any sensitive information that could harm you if inadvertently disclosed.

            This includes, but is not limited to:
          </p>
          <ul>
            <li>Financial Information (bank accounts, credit card numbers)

            </li>
            <li>Personal Health Information (diagnoses, medical records)

            </li>
            <li>Government IDs (social security numbers, driver's license numbers)

            </li>
            <li>Any other private data you would not want another user, including a mentor or pattern creator, to see.

            </li>
          </ul>
          <p>
            You are solely responsible for the information you choose to share during your interactions on the platform.
          </p>
          <p>
            Understanding the Role of Mentors
            We want to be transparent about the nature of mentorship on Clarynext.

            <ul> <li>Mentors are not necessarily licensed professionals. They may not be certified therapists, financial advisors, lawyers, or medical doctors.

           </li><li> Mentors are not bound by professional codes of ethics that licensed professionals are required to follow.

            </li><li>The guidance they offer is based on their personal and professional experience and should be considered as such, not as professional advice.

            </li><li>Any actions or decisions you make based on conversations with mentors are your own responsibility.
            </li>
            </ul>
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. {t("your_rights") || "Your Privacy Rights"}</h2>
          <p className="mb-4">
            You have control over your personal information.

            Access: You can request a copy of the personal data we hold about you.

            Deletion: You can request to delete your account and all associated personal data at any time.

            To exercise these rights, please contact us through our support channels.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. {t("cookies") || "Cookies and Tracking Technologies"}</h2>
          <p className="mb-4">
            We use cookies and similar tracking technologies to track activity on our application and hold certain information.
            Cookies are files with small amount of data which may include an anonymous unique identifier. You can instruct your browser
            to refuse all cookies or to indicate when a cookie is being sent.
          </p>
        </section>


        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. {t("changes_to_privacy_policy") || "Changes to This Privacy Policy"}</h2>
          <p className="mb-4">We may update our Security & Privacy Policy from time to time, especially as the platform evolves. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.


          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">11. {t("contact_us") || "Contact Us"}</h2>
          <p className="mb-4">
            If you have any questions about this Privacy Policy, please contact us through our support channels.
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
